import { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import detectEthereumProvider from '@metamask/detect-provider';
import { getProjectByID } from '../../store/project/projectActions';
import { registrationTokenRequestAction } from '../../store/token/tokenActions';
import { accountDetailsRequestAction } from '../../store/account/userActions';
import {
  checkAllConditionForStake,
  checkRegisterConditionForProject,
} from '../../helpers/verificationHelper';
import wallet from '../../helpers/wallet';
import {
  fetchNftPaymentToken,
  getNftDisplayContractAddress,
  getNftSaleContractAddress,
  isAddress,
} from '../../helpers/nftSale';
import MercurXMintNftModal from '../../components/MercurXMintNftModal/MercurXMintNftModal';
import MercurXSpinner from '../../components/MercurXSpinner/MercurXSpinner';
import './NftSaleProjectDetail.scss';

const formatCountdownValue = (value) => {
  return value.toString().padStart(2, '0');
};

const getCountdown = (targetDate) => {
  const targetTime = new Date(targetDate).getTime();

  if (Number.isNaN(targetTime)) {
    return {
      days: '00',
      hours: '00',
      minutes: '00',
      seconds: '00',
      isEnded: true,
    };
  }

  const diff = targetTime - Date.now();

  if (diff <= 0) {
    return {
      days: '00',
      hours: '00',
      minutes: '00',
      seconds: '00',
      isEnded: true,
    };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return {
    days: formatCountdownValue(days),
    hours: formatCountdownValue(hours),
    minutes: formatCountdownValue(minutes),
    seconds: formatCountdownValue(seconds),
    isEnded: false,
  };
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatUsd = (value) => {
  return toNumber(value).toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'symbol',
    useGrouping: true,
    minimumFractionDigits: 4,
  });
};

const formatNumber = (value) => {
  return toNumber(value).toLocaleString('tr-TR', { useGrouping: true });
};

const getStatusLabel = (status) => {
  if (status === 'active') {
    return 'Active';
  }
  if (status === 'completed') {
    return 'Completed';
  }
  if (status === 'incoming') {
    return 'Incoming';
  }
  return 'Other';
};

const getKycRequirement = () => {
  const savedRequirement = localStorage.getItem('requireKyc');
  return savedRequirement ? JSON.parse(savedRequirement) : false;
};

function NftSaleProjectDetail({
  projectId,
  project,
  user,
  accounts,
  getProjectByIDRequest,
  registrationTokenRequest,
  accountDetailsRequest,
}) {
  const routeProjectId = Number(projectId);
  const hasMatchingProject = Number(project?.id) === routeProjectId;
  const nftData = hasMatchingProject ? project?.nft : null;
  const saleEndDate = hasMatchingProject ? project?.round_sale?.end_date : null;
  const todayUTC = new Date().toISOString();
  const requireKyc = getKycRequirement();
  const hasUserSession = Boolean(JSON.parse(localStorage.getItem('user') || 'null')?.token);
  const displayContractAddress = hasMatchingProject ? getNftDisplayContractAddress(project) : '';
  const saleContractAddress = hasMatchingProject ? getNftSaleContractAddress(project) : '';
  const registrationOpen = project?.round_registration?.end_date > todayUTC;
  const isUserRegistered = Boolean(
    user?.projects?.some((registeredProject) => registeredProject.project_id === project?.id),
  );

  const [countdown, setCountdown] = useState(() => getCountdown(saleEndDate));
  const [showMintModal, setShowMintModal] = useState(false);
  const [paymentToken, setPaymentToken] = useState(null);
  const [addTokenError, setAddTokenError] = useState('');

  const checkStakeConditions = (
    currentUser,
    currentAccounts,
    isControlProject = false,
    currentProject = null,
  ) => {
    return checkAllConditionForStake(
      currentUser,
      currentAccounts,
      isControlProject,
      currentProject,
      requireKyc,
    );
  };

  const checkRegistrationConditions = (
    currentUser,
    currentAccounts,
    isControlProject = false,
    currentProject = null,
  ) => {
    return checkRegisterConditionForProject(
      currentUser,
      currentAccounts,
      isControlProject,
      currentProject,
      requireKyc,
    );
  };

  const canRegister = checkRegistrationConditions(user, accounts) &&
    project?.is_active === 'active' &&
    !isUserRegistered;
  const canMint = checkStakeConditions(user, accounts) &&
    project?.is_active === 'active' &&
    !registrationOpen;

  useEffect(() => {
    if (!routeProjectId) {
      return;
    }

    if (!hasMatchingProject) {
      getProjectByIDRequest({ id: routeProjectId });
    }
  }, [routeProjectId, hasMatchingProject, getProjectByIDRequest]);

  useEffect(() => {
    if (!hasUserSession) {
      return;
    }

    accountDetailsRequest();
  }, [hasUserSession, routeProjectId, accountDetailsRequest]);

  useEffect(() => {
    setPaymentToken(null);

    if (!hasMatchingProject) {
      return;
    }

    let isCancelled = false;
    fetchNftPaymentToken(project).then((token) => {
      if (!isCancelled) {
        setPaymentToken(token);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [hasMatchingProject, project]);

  useEffect(() => {
    setCountdown(getCountdown(saleEndDate));

    if (!saleEndDate) {
      return;
    }

    const interval = setInterval(() => {
      setCountdown(getCountdown(saleEndDate));
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [saleEndDate]);

  if (!routeProjectId || !hasMatchingProject) {
    return <MercurXSpinner />;
  }

  if (!nftData) {
    return (
      <Container className="nft-sale-page mt-4 mt-lg-5 px-3 px-lg-5">
        <Card className="nft-sale-info-card">
          <h1 className="text-fs-head-lg text-light mb-2">NFT data not found</h1>
          <p className="text-fs-body-md text-t-body-color-light mb-0">
            This project does not contain an NFT object.
          </p>
        </Card>
      </Container>
    );
  }

  const totalSupply = toNumber(nftData.total_supply);
  const mintedSupply = toNumber(project?.total_tokens_sold);
  const mintedPercentage = totalSupply
    ? Math.min(100, Math.max(0, Number(((mintedSupply / totalSupply) * 100).toFixed(1))))
    : 0;
  const remainingSupply = Math.max(0, totalSupply - mintedSupply);
  const contractLabel = isAddress(displayContractAddress)
    ? `${displayContractAddress.slice(0, 6)}...${displayContractAddress.slice(-4)}`
    : displayContractAddress || '-';

  const saleMetrics = [
    {
      label: 'Mint Price',
      value: `${toNumber(nftData.price_in_mercurx)} MERX`,
      subValue: formatUsd(nftData.price_in_usd),
    },
    {
      label: 'Total Supply',
      value: formatNumber(totalSupply),
      subValue: `${formatNumber(remainingSupply)} remaining`,
    },
    {
      label: 'Minted',
      value: formatNumber(mintedSupply),
      subValue: `${mintedPercentage}% sold`,
    },
    {
      label: 'Participants',
      value: formatNumber(project?.number_of_participants),
      subValue: project?.round_registration
        ? `${formatNumber(project?.number_of_registrations)} registrations`
        : 'Unique wallets',
    },
  ];

  const countdownUnits = [
    { label: 'Days', value: countdown.days },
    { label: 'Hours', value: countdown.hours },
    { label: 'Minutes', value: countdown.minutes },
    { label: 'Seconds', value: countdown.seconds },
  ];

  const handleConnectWallet = async () => {
    await wallet.connectWallet();
  };

  const handleRegistration = () => {
    registrationTokenRequest({ project_id: project?.id });
  };

  const openMintModal = () => {
    setShowMintModal(true);
  };

  const closeMintModal = () => {
    setShowMintModal(false);
  };

  const handleAddPaymentToken = async () => {
    setAddTokenError('');
    if (!paymentToken) {
      return;
    }
    try {
      const provider = await detectEthereumProvider();
      if (!provider) {
        setAddTokenError('No wallet provider found. Please install MetaMask.');
        return;
      }
      await provider.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: paymentToken,
        },
      });
    } catch (error) {
      setAddTokenError(`Token addition error: ${error.message}`);
    }
  };

  return (
    <Container className="nft-sale-page mt-4 mt-lg-5 px-3 px-lg-5">
      <Row className="g-4">
        <Col xl={7}>
          <Card className="nft-sale-artwork-card">
            <div className="nft-sale-artwork-glow"></div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Badge className="nft-sale-launch-badge">NFT Launch</Badge>
              <span className="text-fs-body-sm text-light">Project #{projectId}</span>
            </div>
            <div className="nft-sale-artwork-wrap">
              <img
                src={`${process.env.REACT_APP_API_URL}/projects/${project?.id}/image`}
                alt={nftData?.name || project?.name}
                className="nft-sale-artwork-image"
              />
            </div>
            <div
              className={[
                'd-flex',
                'justify-content-between',
                'align-items-center',
                'mt-3',
                'nft-sale-artwork-meta',
              ].join(' ')}
            >
              <div>
                <div className="text-fs-body-sm text-t-body-color-light">Collection</div>
                <div className="text-fs-head-xs text-light">
                  {nftData?.symbol || nftData?.name || 'NFT'}
                </div>
              </div>
              <div className="text-end">
                <div className="text-fs-body-sm text-t-body-color-light">Contract</div>
                {isAddress(displayContractAddress) ? (
                  <a
                    href={`https://testnet.bscscan.com/address/${displayContractAddress}`}
                    className="text-fs-head-xs text-light text-decoration-none"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {contractLabel}
                  </a>
                ) : (
                  <div className="text-fs-head-xs text-light">{contractLabel}</div>
                )}
              </div>
            </div>
          </Card>
        </Col>

        <Col xl={5}>
          <Card className="nft-sale-info-card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Badge className="nft-sale-status-badge">{getStatusLabel(project?.is_active)}</Badge>
              <span className="text-fs-body-sm text-light">NFT Sale</span>
            </div>

            <h1 className="text-fs-head-lg text-light mb-2">{nftData?.name || project?.name}</h1>
            <p className="text-fs-body-md text-t-body-color-light mb-3">
              {project?.explanation_text || '-'}
            </p>

            <div className="nft-sale-metrics-grid">
              {saleMetrics.map((metric) => {
                return (
                  <div className="nft-sale-metric-card" key={metric.label}>
                    <div className="nft-sale-metric-label">{metric.label}</div>
                    <div className="nft-sale-metric-value">{metric.value}</div>
                    <div className="nft-sale-metric-subvalue">{metric.subValue}</div>
                  </div>
                );
              })}
            </div>

            <div className="nft-sale-countdown-wrap mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="text-fs-head-xxs text-light">Sale Countdown</div>
                <div className="text-fs-body-sm text-t-body-color-light">
                  {countdown.isEnded ? 'Sale ended' : project?.round_time_left || 'Ending soon'}
                </div>
              </div>
              <div className="nft-sale-countdown-grid">
                {countdownUnits.map((unit) => {
                  return (
                    <div className="nft-sale-countdown-item" key={unit.label}>
                      <div className="nft-sale-countdown-value">{unit.value}</div>
                      <div className="nft-sale-countdown-label">{unit.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-3">
              <div className="nft-sale-progress-track">
                <div
                  className="nft-sale-progress-fill"
                  style={{ width: `${mintedPercentage}%` }}
                ></div>
              </div>
              <div className="d-flex justify-content-between mt-2">
                <span className="text-fs-body-sm text-t-body-color-light">Minted progress</span>
                <span className="text-fs-body-sm text-light">{mintedPercentage}%</span>
              </div>
            </div>

            <Row className="g-2 mt-3">
              {!accounts?.[0] ? (
                <Col sm={12}>
                  <Button className="nft-sale-cta-primary w-100" onClick={handleConnectWallet}>
                    Connect Wallet
                  </Button>
                </Col>
              ) : registrationOpen ? (
                <Col sm={12}>
                  <Button
                    className="nft-sale-cta-primary w-100"
                    onClick={handleRegistration}
                    disabled={!canRegister}
                  >
                    {isUserRegistered ? 'Registered' : 'Registration'}
                  </Button>
                </Col>
              ) : (
                <Col sm={12}>
                  <Button
                    className="nft-sale-cta-primary w-100"
                    onClick={openMintModal}
                    disabled={!canMint}
                  >
                    Mint Now
                  </Button>
                </Col>
              )}
              {isAddress(saleContractAddress || displayContractAddress) && (
                <Col sm={12}>
                  <Button
                    as="a"
                    href={
                      `https://testnet.bscscan.com/address/` +
                      `${saleContractAddress || displayContractAddress}`
                    }
                    className="nft-sale-cta-secondary w-100"
                    target="_blank"
                    rel="noreferrer"
                  >
                    View Contract
                  </Button>
                </Col>
              )}
            </Row>

            <div className="text-fs-body-sm text-t-body-color-light mt-3">
              {!accounts?.[0]
                ? 'Connect your wallet to continue with registration or mint.'
                : registrationOpen
                  ? 'Registration phase is active. Mint opens after registration ends.'
                  : 'Account verification, wallet connection, and optional KYC are required ' +
                  'before mint.'}
            </div>

            {paymentToken && (
              <div className="nft-sale-payment-token mt-3">
                <div className="d-flex justify-content-between align-items-center gap-2">
                  <div>
                    <div className="nft-sale-phase-title">Payment Token</div>
                    <a
                      href={`https://testnet.bscscan.com/token/${paymentToken.address}`}
                      className="text-fs-body-sm text-light text-decoration-none"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {paymentToken.symbol} · {paymentToken.address.slice(0, 6)}...
                      {paymentToken.address.slice(-4)}
                    </a>
                  </div>
                  <Button
                    className="nft-sale-cta-secondary nft-sale-add-token-button"
                    onClick={handleAddPaymentToken}
                  >
                    Add {paymentToken.symbol}
                  </Button>
                </div>
                {addTokenError && (
                  <div className="text-fs-body-sm text-danger mt-2">{addTokenError}</div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {showMintModal && (
        <MercurXMintNftModal show={showMintModal} onHide={closeMintModal} />
      )}
    </Container>
  );
}

const mapStateToProps = (state) => {
  return {
    project: state.projectReducer.project,
    user: state.userReducer.user,
    accounts: state.walletReducer.accounts,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getProjectByIDRequest: (payload) => {
      dispatch(getProjectByID(payload));
    },
    registrationTokenRequest: (payload) => {
      dispatch(registrationTokenRequestAction(payload));
    },
    accountDetailsRequest: (payload) => {
      dispatch(accountDetailsRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(NftSaleProjectDetail);
